// Simplified IndexedDB Manager for Essential Offline Data
// Stores only user authentication data and profile information locally per user

import { User } from '@supabase/supabase-js';
import { offlineConfig } from '../config/offlineConfig';
import { UserRole } from '../hooks/useOfflineUserRole';
import { supabase } from './supabaseClient';

// Database configuration (using offline config)
const DB_PREFIX = 'user_';
const DB_VERSION = offlineConfig.getIndexedDBConfig().version + 1;
const USERS_STORE = 'users';

// Essential tables for offline storage - only user and profile data
export const ALTZERO_TABLES = [
  'base_profiles',
  'base_organisation_members',
  'altzero_spaces',
  'altzero_bases',
  'altzero_tables',
  'altzero_records',
] as const;

export type AltzeroTable = (typeof ALTZERO_TABLES)[number];

// Helper functions for database setup (used in onupgradeneeded callback)
function getPrimaryKeyForTable(tableName: string): string {
  const primaryKeys: Record<string, string> = {
    base_profiles: 'id',
    base_organisation_members: 'id',
    altzero_spaces: 'id',
    altzero_bases: 'id',
    altzero_tables: 'id',
    altzero_records: 'id',
  };

  return primaryKeys[tableName] || 'id'; // Default to 'id'
}

function createIndexesForTable(store: IDBObjectStore, tableName: string): void {
  const commonIndexes = ['created_at', 'updated_at', 'tenant_id', 'user_id'];

  // Table-specific indexes
  const tableIndexes: Record<string, string[]> = {
    base_profiles: ['email', 'role', 'tenant_id'],
    base_organisation_members: ['user_id', 'organisation_id', 'is_active'],
    altzero_spaces: ['organizationId'],
    altzero_bases: ['spaceId'],
    altzero_tables: ['baseId'],
    altzero_records: ['tableId'],
  };

  const indexes = [...commonIndexes, ...(tableIndexes[tableName] || [])];

  indexes.forEach(indexName => {
    try {
      store.createIndex(indexName, indexName, { unique: false });
    } catch (error) {
      // Index might already exist or field might not exist in this table
      console.debug(
        `[OfflineDB] Could not create index ${indexName} for ${tableName}`
      );
    }
  });
}

// Interfaces for cached data
export interface CachedSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user_id: string;
}

export interface UserProfile {
  id: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface CachedUser {
  id: string; // Primary key (user ID)
  email: string;
  userData: User; // Full Supabase user object
  profile: UserProfile | null;
  role: UserRole;
  session: CachedSession | null;
  lastSync: number; // Timestamp of last sync with server
  isOfflineEnabled: boolean;
  offlineCapabilities: string[];
}

// Metadata for sync tracking
export interface SyncMetadata {
  tableName: string;
  lastSync: number;
  recordCount: number;
  checksum?: string;
}

// Mutation queue for offline changes
export interface PendingMutation {
  id: string;
  tableName: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  data: any;
  timestamp: number;
  userId: string;
}

export class OfflineDBManager {
  private static instances: Map<string, OfflineDBManager> = new Map();
  public db: IDBDatabase | null = null; // Made public for OfflineFirstDataManager access
  private isInitialized = false;
  private userId: string;
  private dbName: string;

  private constructor(userId: string) {
    this.userId = userId;
    this.dbName = `${DB_PREFIX}${userId}`;
  }

  static getInstance(userId: string): OfflineDBManager {
    if (!OfflineDBManager.instances.has(userId)) {
      OfflineDBManager.instances.set(userId, new OfflineDBManager(userId));
    }
    return OfflineDBManager.instances.get(userId)!;
  }

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      console.log(`[OfflineDB] Initializing database for user: ${this.userId}`);
      const request = indexedDB.open(this.dbName, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineDB] Failed to open database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        console.log(
          `[OfflineDB] Database initialized successfully: ${this.dbName}`
        );
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        console.log(
          '[OfflineDB] Creating object stores for essential user data only...'
        );

        // Create users store for authentication and session data
        if (!db.objectStoreNames.contains(USERS_STORE)) {
          const userStore = db.createObjectStore(USERS_STORE, {
            keyPath: 'id',
          });

          // Create indexes for efficient querying
          userStore.createIndex('email', 'email', { unique: false });
          userStore.createIndex('role', 'role', { unique: false });
          userStore.createIndex('lastSync', 'lastSync', { unique: false });

          console.log('[OfflineDB] Created users store with indexes');
        }

        // Create object stores for essential altzero tables (only base_profiles)
        ALTZERO_TABLES.forEach(tableName => {
          if (!db.objectStoreNames.contains(tableName)) {
            const store = db.createObjectStore(tableName, {
              keyPath: getPrimaryKeyForTable(tableName),
            });

            // Create indexes for common query patterns
            createIndexesForTable(store, tableName);

            console.log(`[OfflineDB] Created store: ${tableName}`);
          }
        });

        // Create metadata store for sync tracking
        if (!db.objectStoreNames.contains('_sync_metadata')) {
          const metaStore = db.createObjectStore('_sync_metadata', {
            keyPath: 'tableName',
          });
          console.log('[OfflineDB] Created sync metadata store');
        }

        // Create mutation queue store for offline changes
        if (!db.objectStoreNames.contains('_pending_mutations')) {
          const mutationStore = db.createObjectStore('_pending_mutations', {
            keyPath: 'id',
          });
          mutationStore.createIndex('timestamp', 'timestamp', {
            unique: false,
          });
          mutationStore.createIndex('tableName', 'tableName', {
            unique: false,
          });
          console.log('[OfflineDB] Created pending mutations store');
        }

        // Create cached invitations store
        if (!db.objectStoreNames.contains('_cached_invitations')) {
          const invitationStore = db.createObjectStore('_cached_invitations', {
            keyPath: 'id',
          });
          invitationStore.createIndex('type', 'type', { unique: false });
          invitationStore.createIndex('updated_at', 'updated_at', {
            unique: false,
          });
          console.log('[OfflineDB] Created cached invitations store');
        }

        // Create invitation actions store
        if (!db.objectStoreNames.contains('_invitation_actions')) {
          const actionStore = db.createObjectStore('_invitation_actions', {
            keyPath: 'id',
          });
          actionStore.createIndex('invitation_id', 'invitation_id', {
            unique: false,
          });
          actionStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('[OfflineDB] Created invitation actions store');
        }

        // --- Altzero Spaces Stores ---

        // Spaces (from base_tenants)
        if (!db.objectStoreNames.contains('altzero_spaces')) {
          const spaceStore = db.createObjectStore('altzero_spaces', {
            keyPath: 'id',
          });
          spaceStore.createIndex('organizationId', 'organizationId', {
            unique: false,
          });
          console.log('[OfflineDB] Created altzero_spaces store');
        }

        // Bases
        if (!db.objectStoreNames.contains('altzero_bases')) {
          const baseStore = db.createObjectStore('altzero_bases', {
            keyPath: 'id',
          });
          baseStore.createIndex('spaceId', 'spaceId', { unique: false });
          console.log('[OfflineDB] Created altzero_bases store');
        }

        // Tables
        if (!db.objectStoreNames.contains('altzero_tables')) {
          const tableStore = db.createObjectStore('altzero_tables', {
            keyPath: 'id',
          });
          tableStore.createIndex('baseId', 'baseId', { unique: false });
          console.log('[OfflineDB] Created altzero_tables store');
        }

        // Records
        if (!db.objectStoreNames.contains('altzero_records')) {
          const recordStore = db.createObjectStore('altzero_records', {
            keyPath: 'id',
          });
          recordStore.createIndex('tableId', 'tableId', { unique: false });
          console.log('[OfflineDB] Created altzero_records store');
        }
      };
    });
  }

  /**
   * Store user data in IndexedDB
   */
  async storeUser(cachedUser: CachedUser): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([USERS_STORE], 'readwrite');
      const store = transaction.objectStore(USERS_STORE);

      // Add timestamp
      cachedUser.lastSync = Date.now();

      const request = store.put(cachedUser);

      request.onsuccess = () => {
        console.log('[OfflineDB] User stored successfully:', cachedUser.id);
        resolve();
      };

      request.onerror = () => {
        console.error('[OfflineDB] Failed to store user:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get user data from IndexedDB
   */
  async getUser(userId: string): Promise<CachedUser | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([USERS_STORE], 'readonly');
      const store = transaction.objectStore(USERS_STORE);
      const request = store.get(userId);

      request.onsuccess = () => {
        const user = request.result as CachedUser | undefined;
        console.log('[OfflineDB] Retrieved user:', userId, !!user);
        resolve(user || null);
      };

      request.onerror = () => {
        console.error('[OfflineDB] Failed to get user:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get user by email
   */
  async getUserByEmail(email: string): Promise<CachedUser | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([USERS_STORE], 'readonly');
      const store = transaction.objectStore(USERS_STORE);
      const index = store.index('email');
      const request = index.get(email);

      request.onsuccess = () => {
        const user = request.result as CachedUser | undefined;
        console.log('[OfflineDB] Retrieved user by email:', email, !!user);
        resolve(user || null);
      };

      request.onerror = () => {
        console.error(
          '[OfflineDB] Failed to get user by email:',
          request.error
        );
        reject(request.error);
      };
    });
  }

  /**
   * Update user profile data
   */
  async updateUserProfile(
    userId: string,
    profile: Partial<UserProfile>
  ): Promise<void> {
    const user = await this.getUser(userId);
    if (!user) {
      throw new Error(`User not found: ${userId}`);
    }

    user.profile = { ...user.profile, ...profile } as UserProfile;
    user.lastSync = Date.now();

    await this.storeUser(user);
  }

  /**
   * Update user role
   */
  async updateUserRole(userId: string, role: UserRole): Promise<void> {
    const user = await this.getUser(userId);
    if (!user) {
      throw new Error(`User not found: ${userId}`);
    }

    user.role = role;
    user.lastSync = Date.now();

    await this.storeUser(user);
  }

  /**
   * Update user session
   */
  async updateUserSession(
    userId: string,
    session: CachedSession | null
  ): Promise<void> {
    const user = await this.getUser(userId);
    if (!user) {
      throw new Error(`User not found: ${userId}`);
    }

    user.session = session;
    user.lastSync = Date.now();

    await this.storeUser(user);
  }

  /**
   * Check if user session is valid (not expired)
   */
  async isSessionValid(userId: string): Promise<boolean> {
    const user = await this.getUser(userId);
    if (!user || !user.session) {
      return false;
    }

    const now = Date.now() / 1000; // Convert to seconds
    return user.session.expires_at > now;
  }

  /**
   * Get all cached users
   */
  async getAllUsers(): Promise<CachedUser[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([USERS_STORE], 'readonly');
      const store = transaction.objectStore(USERS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result as CachedUser[]);
      };

      request.onerror = () => {
        console.error('[OfflineDB] Failed to get all users:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Remove user from cache
   */
  async removeUser(userId: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([USERS_STORE], 'readwrite');
      const store = transaction.objectStore(USERS_STORE);
      const request = store.delete(userId);

      request.onsuccess = () => {
        console.log('[OfflineDB] User removed:', userId);
        resolve();
      };

      request.onerror = () => {
        console.error('[OfflineDB] Failed to remove user:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clear all cached data
   */
  async clearAll(): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([USERS_STORE], 'readwrite');
      const store = transaction.objectStore(USERS_STORE);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[OfflineDB] All data cleared');
        resolve();
      };

      request.onerror = () => {
        console.error('[OfflineDB] Failed to clear data:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get database statistics
   */
  async getStats(): Promise<{ userCount: number; totalSize: number }> {
    const users = await this.getAllUsers();
    const userCount = users.length;

    // Rough size calculation (in bytes)
    const totalSize = JSON.stringify(users).length;

    return { userCount, totalSize };
  }

  /**
   * Get primary key for each table
   */
  private getPrimaryKey(tableName: string): string {
    return getPrimaryKeyForTable(tableName);
  }

  /**
   * Create indexes for efficient querying
   */
  private createIndexesForTable(
    store: IDBObjectStore,
    tableName: string
  ): void {
    return createIndexesForTable(store, tableName);
  }

  /**
   * Ensure database is initialized before operations
   */
  private async ensureInitialized(): Promise<void> {
    console.log(
      `[OfflineDB] ensureInitialized() called, isInitialized: ${this.isInitialized}`
    );
    if (!this.isInitialized) {
      console.log(`[OfflineDB] Database not initialized, calling init()`);
      await this.init();
    } else {
      console.log(`[OfflineDB] Database already initialized`);
    }
  }

  /**
   * Public method to ensure database is initialized (for external access)
   */
  async ensureReady(): Promise<void> {
    await this.ensureInitialized();
  }

  /**
   * Perform initial sync - download all user-accessible data
   */
  async performInitialSync(): Promise<void> {
    if (!navigator.onLine) {
      console.log(
        '[OfflineDB] Skipping initial sync - offline mode. Using cached data.'
      );
      return;
    }

    console.log('[OfflineDB] Starting initial sync for all tables...');

    for (const tableName of ALTZERO_TABLES) {
      try {
        await this.syncTable(tableName, this.userId);
        console.log(`[OfflineDB] Synced table: ${tableName}`);
      } catch (error) {
        console.error(`[OfflineDB] Failed to sync table ${tableName}:`, error);
        // Continue with other tables even if one fails
      }
    }

    console.log('[OfflineDB] Initial sync completed');
  }

  /**
   * Sync a specific table from Supabase with user-specific filtering
   */
  private async syncTable(tableName: string, userId: string): Promise<void> {
    try {
      // CRITICAL: Don't sync if offline - preserve existing cached data
      if (!navigator.onLine) {
        console.log(
          `[OfflineDB] Skipping sync for ${tableName} - offline mode. Preserving cached data.`
        );
        return;
      }

      let query = supabase.schema('altzero').from(tableName).select('*');

      // Apply user-specific filtering based on table type
      if (tableName === 'base_profiles') {
        // Only sync the current user's profile
        query = query.eq('id', userId);
      } else if (tableName === 'base_organisation_members') {
        // Only sync the current user's organization memberships
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error(`[OfflineDB] Error fetching ${tableName}:`, error);
        // CRITICAL: Don't clear data on error - preserve existing cache
        return;
      }

      if (!data || data.length === 0) {
        console.log(
          `[OfflineDB] No data found for table: ${tableName} (user: ${userId})`
        );
        // CRITICAL: Don't clear data if no data returned - preserve existing cache
        return;
      }

      // Store data in IndexedDB
      await this.bulkInsert(tableName, data);

      // Update sync metadata
      await this.updateSyncMetadata(tableName, data.length);
    } catch (error) {
      console.error(`[OfflineDB] Failed to sync table ${tableName}:`, error);
      // CRITICAL: Don't throw - preserve existing data
      return;
    }
  }

  /**
   * Incremental sync for a specific table - only sync records updated since lastSync
   */
  async incrementalSyncTable(
    tableName: AltzeroTable,
    userId: string,
    lastSync: number
  ): Promise<void> {
    try {
      // CRITICAL: Don't sync if offline - preserve existing cached data
      if (!navigator.onLine) {
        console.log(
          `[OfflineDB] Skipping incremental sync for ${tableName} - offline mode. Preserving cached data.`
        );
        return;
      }

      let query = supabase
        .schema('altzero')
        .from(tableName)
        .select('*')
        .gt('updated_at', new Date(lastSync).toISOString());

      // Apply user-specific filtering based on table type
      if (tableName === 'base_profiles') {
        // Only sync the current user's profile
        query = query.eq('id', userId);
      } else if (tableName === 'base_organisation_members') {
        // Only sync the current user's organization memberships
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error(
          `[OfflineDB] Error fetching incremental updates for ${tableName}:`,
          error
        );
        // CRITICAL: Don't clear data on error - preserve existing cache
        return;
      }

      if (!data || data.length === 0) {
        console.log(
          `[OfflineDB] No incremental updates for table: ${tableName} (user: ${userId})`
        );
        return;
      }

      // Upsert data into IndexedDB (update existing, insert new)
      await this.bulkUpsert(tableName, data);

      // Update sync metadata
      await this.updateSyncMetadata(tableName, data.length);

      console.log(
        `[OfflineDB] Incremental sync completed for ${tableName}: ${data.length} records`
      );
    } catch (error) {
      console.error(
        `[OfflineDB] Failed to incremental sync table ${tableName}:`,
        error
      );
      // CRITICAL: Don't throw - preserve existing data
      return;
    }
  }

  /**
   * Bulk insert data into a table
   */
  private async bulkInsert(tableName: string, data: any[]): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([tableName], 'readwrite');
      const store = transaction.objectStore(tableName);

      // CRITICAL: Don't clear if no data to insert - preserve existing cache
      if (data.length === 0) {
        console.warn(
          `[OfflineDB] bulkInsert called with empty data for ${tableName} - skipping clear to preserve existing data`
        );
        resolve();
        return;
      }

      // Clear existing data first
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        // Insert all new data
        let completed = 0;
        const total = data.length;

        data.forEach(record => {
          const request = store.add(record);

          request.onsuccess = () => {
            completed++;
            if (completed === total) {
              console.log(
                `[OfflineDB] Bulk inserted ${total} records into ${tableName}`
              );
              resolve();
            }
          };

          request.onerror = () => {
            console.error(
              `[OfflineDB] Failed to insert record into ${tableName}:`,
              request.error
            );
            reject(request.error);
          };
        });
      };

      clearRequest.onerror = () => {
        console.error(
          `[OfflineDB] Failed to clear table ${tableName}:`,
          clearRequest.error
        );
        reject(clearRequest.error);
      };
    });
  }

  /**
   * Bulk upsert data into a table (update existing, insert new)
   */
  private async bulkUpsert(tableName: string, data: any[]): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([tableName], 'readwrite');
      const store = transaction.objectStore(tableName);

      let completed = 0;
      const total = data.length;

      if (total === 0) {
        resolve();
        return;
      }

      data.forEach(record => {
        // Use put() which will update if exists, insert if not
        const request = store.put(record);

        request.onsuccess = () => {
          completed++;
          if (completed === total) {
            console.log(
              `[OfflineDB] Bulk upserted ${total} records into ${tableName}`
            );
            resolve();
          }
        };

        request.onerror = () => {
          console.error(
            `[OfflineDB] Failed to upsert record into ${tableName}:`,
            request.error
          );
          reject(request.error);
        };
      });
    });
  }

  /**
   * Update sync metadata for a table
   */
  private async updateSyncMetadata(
    tableName: string,
    recordCount: number
  ): Promise<void> {
    await this.ensureInitialized();

    const metadata: SyncMetadata = {
      tableName,
      lastSync: Date.now(),
      recordCount,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['_sync_metadata'], 'readwrite');
      const store = transaction.objectStore('_sync_metadata');
      const request = store.put(metadata);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get sync metadata for a table
   */
  async getSyncMetadata(tableName: string): Promise<SyncMetadata | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['_sync_metadata'], 'readonly');
      const store = transaction.objectStore('_sync_metadata');
      const request = store.get(tableName);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error(
          `[OfflineDB] Failed to get sync metadata for ${tableName}:`,
          request.error
        );
        reject(request.error);
      };
    });
  }

  /**
   * Get all records from a table
   */
  async getAll(tableName: AltzeroTable): Promise<any[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([tableName], 'readonly');
      const store = transaction.objectStore(tableName);
      const request = store.getAll();

      request.onsuccess = () => {
        console.log(
          `[OfflineDB] Retrieved ${request.result.length} records from ${tableName}`
        );
        resolve(request.result);
      };

      request.onerror = () => {
        console.error(
          `[OfflineDB] Failed to get all from ${tableName}:`,
          request.error
        );
        reject(request.error);
      };
    });
  }

  /**
   * Get a single record by ID
   */
  async getById(tableName: AltzeroTable, id: string): Promise<any | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([tableName], 'readonly');
      const store = transaction.objectStore(tableName);
      const request = store.get(id);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        console.error(
          `[OfflineDB] Failed to get record from ${tableName}:`,
          request.error
        );
        reject(request.error);
      };
    });
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.isInitialized = false;
      console.log('[OfflineDB] Database connection closed');
    }
  }
}

// Export function to get user-specific instance
export const getOfflineDB = (userId: string) => {
  console.log(`[OfflineDB] getOfflineDB() called for user: ${userId}`);
  const instance = OfflineDBManager.getInstance(userId);
  console.log(`[OfflineDB] Returning instance for user: ${userId}`);
  return instance;
};
